/**
 * A17 — Treasury Operations Dashboard
 *
 * Surfaces the four Treasury-Safe ops widgets from
 * COYNITT_GNOSIS_SAFE_INTEGRATION_REVIEW.md §A17:
 *
 *   1. Float gauge — current Normal / Warning / Minimum / Critical state
 *      with available / pending-refunds / target breakdown.
 *   2. Threshold-breach event log — last 50 transitions from
 *      `float_status_transitions`.
 *   3. Reversal queue — `reversals` where state in (requested, in_progress);
 *      compliance officers / super-admins can approve / deny.
 *   4. Pending Safe co-signs — `safe_tx_queue` where state = awaiting_cosign;
 *      shows disbursement-id, amount, recipient, time pending.
 *
 * Access gate: `requirePage('treasury')` — see usePagePermissions.js.
 * Stanford as super-admin per project_coynitt_dashboard_access.md.
 *
 * Demo isolation: dashboard NEVER shows demo data. The backend services for
 * A14/A16 short-circuit demo users before writing to `disbursements`,
 * `reversals`, `safe_tx_queue`, or `fintrac_*_log` — so by construction the
 * tables below contain only real data. As a defence-in-depth, we still drop
 * any row whose recipient/safeTxHash starts with `0xDEM*` / `0xDEAD*`.
 *
 * Design system: no shadows, no gradients, ≤5px radius cards. Uses existing
 * `card`, `badge`, `btn` primitives from the dashboard CSS.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import clsx from 'clsx';
import { useOutletContext } from 'react-router-dom';
import Icon from '../components/Icon';
import { useAuth } from '../contexts/AuthContext';
import { usePagePermissions } from '../hooks/usePagePermissions';
import {
  fetchTreasuryFloatStatus,
  fetchTreasuryFloatTransitions,
  fetchTreasuryReversalQueue,
  fetchTreasuryPendingCosigns,
  approveReversal,
  denyReversal,
} from '../services/api';
import { timeAgo } from '../utils/format';

const STATE_META = {
  normal:   { label: 'Normal',   accent: 'var(--ok)',   icon: 'shield' },
  warning:  { label: 'Warning',  accent: 'var(--warn)', icon: 'bell' },
  minimum:  { label: 'Minimum',  accent: 'var(--err)',  icon: 'bell' },
  critical: { label: 'Critical', accent: 'var(--err)',  icon: 'bell' },
};

const CATEGORY_BADGE = {
  A:            { label: 'A — Pre-Disbursement',  cls: 'badge-info' },
  B:            { label: 'B — Pre-Conversion',    cls: 'badge-warning' },
  C:            { label: 'C — Post-Conversion',   cls: 'badge-danger' },
  unclassified: { label: 'Unclassified',          cls: 'badge-muted' },
};

const REVERSAL_STATE_BADGE = {
  requested:   'badge-info',
  classified:  'badge-info',
  in_progress: 'badge-warning',
  completed:   'badge-muted',
  failed:      'badge-danger',
};

const fmtUsdc = (n) => `$${(parseFloat(n) || 0).toFixed(2)}`;
const isDemoLike = (s) =>
  !!s && (s.toLowerCase().startsWith('0xdem0') || s.toLowerCase().startsWith('0xdead'));

const Treasury = () => {
  const { environment } = useOutletContext();
  const { profile } = useAuth();
  const { isSuperAdmin } = usePagePermissions();
  // Approving / denying reversals is gated on super-admin or compliance officer.
  const canActOnReversals =
    isSuperAdmin || profile?.role === 'compliance_officer';

  const [floatStatus, setFloatStatus] = useState(null);
  const [transitions, setTransitions] = useState([]);
  const [reversals, setReversals] = useState([]);
  const [cosigns, setCosigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t, r, c] = await Promise.all([
        fetchTreasuryFloatStatus().catch(() => null),
        fetchTreasuryFloatTransitions(50).catch(() => []),
        fetchTreasuryReversalQueue({ state: 'open' }).catch(() => []),
        fetchTreasuryPendingCosigns().catch(() => []),
      ]);
      setFloatStatus(s);
      setTransitions(Array.isArray(t) ? t : []);
      setReversals((Array.isArray(r) ? r : []).filter((row) => !isDemoLike(row.onChainTxHash)));
      setCosigns((Array.isArray(c) ? c : []).filter((row) => !isDemoLike(row.recipient)));
      setError(null);
    } catch (err) {
      setError(err?.message || 'Failed to load Treasury data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await loadAll();
    })();
    return () => { cancelled = true; };
  }, [loadAll, environment]);

  const state = floatStatus?.state || 'normal';
  const meta = STATE_META[state] || STATE_META.normal;

  const updateReversal = async (reversalId, action) => {
    const prev = reversals;
    setReversals((p) => p.filter((r) => r.reversalId !== reversalId));
    try {
      if (action === 'approve') {
        await approveReversal(reversalId, { officerUid: profile?.id, notes: 'approved via dashboard' });
      } else {
        await denyReversal(reversalId, { officerUid: profile?.id, notes: 'denied via dashboard' });
      }
    } catch (err) {
      setReversals(prev);
      setError(`Failed to ${action} reversal: ${err?.message || 'unknown error'}`);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Treasury</h1>
          <p>
            Float monitor, reversal queue, and pending Safe co-signs
            <span className="text-tx3"> · {environment}</span>
          </p>
        </div>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            background: 'var(--err-bg)',
            color: 'var(--err)',
            padding: 'var(--s3)',
            borderRadius: 'var(--r-md)',
            fontSize: 13,
            marginBottom: 'var(--s3)',
          }}
        >
          {error}
        </div>
      )}

      <FloatGauge floatStatus={floatStatus} state={state} meta={meta} loading={loading} />

      <TransitionLog transitions={transitions} loading={loading} />

      <ReversalQueue
        reversals={reversals}
        canAct={canActOnReversals}
        onAction={updateReversal}
        loading={loading}
      />

      <CosignQueue cosigns={cosigns} loading={loading} />
    </>
  );
};

// =============================================================================
// FLOAT GAUGE
// =============================================================================

const FloatGauge = ({ floatStatus, state, meta, loading }) => {
  if (loading && !floatStatus) {
    return (
      <div className="dashboard-grid stagger mb-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="card"><div className="skeleton skeleton-block" style={{ height: 60 }} /></div>
        ))}
      </div>
    );
  }
  if (!floatStatus) {
    return (
      <div className="card mb-4">
        <div className="empty">
          <div className="empty-icon"><Icon name="shield" size={26} /></div>
          <h3>Float monitor offline</h3>
          <p>The Float Monitor service (A13) has not posted a status yet. This is expected before A4.5 (Treasury Safe deploy) lands on Sepolia.</p>
        </div>
      </div>
    );
  }
  const t = floatStatus.thresholds || {};
  return (
    <>
      <div className="dashboard-grid stagger mb-4">
        <div className="card">
          <div className="card-title">Float State</div>
          <div
            className="stat-value"
            style={{
              color: meta.accent,
              animation: (state === 'minimum' || state === 'critical')
                ? 'notifPulse 1.6s var(--ease) infinite'
                : 'none',
            }}
          >
            {meta.label}
          </div>
          <div className="stat-label">
            {floatStatus.circuitBreakerOpen ? 'Circuit breaker OPEN' : 'Circuit breaker closed'}
          </div>
        </div>
        <div className="card">
          <div className="card-title">Available USDC</div>
          <div className="stat-value" style={{ color: 'var(--teal)' }}>
            {fmtUsdc(floatStatus.availableUsdc)}
          </div>
          <div className="stat-label">
            Balance {fmtUsdc(floatStatus.balanceUsdc)} − pending {fmtUsdc(floatStatus.pendingRefundsUsdc)}
          </div>
        </div>
        <div className="card">
          <div className="card-title">Target Float (3x peak)</div>
          <div className="stat-value">{fmtUsdc(t.target)}</div>
          <div className="stat-label">Peak daily {fmtUsdc(floatStatus.peakDailyUsdc)}</div>
        </div>
        <div className="card">
          <div className="card-title">Breaker Line (1x peak)</div>
          <div className="stat-value" style={{ color: 'var(--err)' }}>{fmtUsdc(t.minimum)}</div>
          <div className="stat-label">Below this: outbound paused</div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-title">Treasury Safe</div>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--tx1)', marginTop: 8 }}>
          {floatStatus.treasurySafeAddress || '—'}
        </div>
        <div className="text-tx3 text-xs" style={{ marginTop: 4 }}>
          Multisig per Coynitt Gnosis Safe Policy v1.0 §2.1 (2-of-2 Sepolia, 2-of-3 mainnet).
        </div>
      </div>
    </>
  );
};

// =============================================================================
// TRANSITION LOG
// =============================================================================

const TransitionLog = ({ transitions, loading }) => {
  if (loading && transitions.length === 0) {
    return (
      <div className="card mb-4">
        <div className="card-title">Threshold-Breach Events</div>
        <div className="skeleton skeleton-block" style={{ height: 100, marginTop: 12 }} />
      </div>
    );
  }
  return (
    <div className="card mb-4">
      <div className="card-title">Threshold-Breach Events (last 50)</div>
      {transitions.length === 0 ? (
        <div className="text-tx3 text-sm" style={{ marginTop: 12 }}>
          No transitions recorded yet.
        </div>
      ) : (
        <div className="stagger" style={{ display: 'grid', gap: 4, marginTop: 12 }}>
          {transitions.map((t, i) => {
            const fromMeta = STATE_META[t.from] || STATE_META.normal;
            const toMeta = STATE_META[t.to] || STATE_META.normal;
            return (
              <div
                key={`${t.timestamp?._seconds || i}-${i}`}
                className="row row-gap-3"
                style={{
                  padding: '8px 12px',
                  borderRadius: 4,
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--bd)',
                  alignItems: 'center',
                }}
              >
                <span style={{ color: fromMeta.accent, fontSize: 12 }}>{fromMeta.label}</span>
                <span className="text-tx3">→</span>
                <span style={{ color: toMeta.accent, fontSize: 12, fontWeight: 600 }}>{toMeta.label}</span>
                <span className="text-tx3 text-xs flex-1">
                  Available {fmtUsdc(t.status?.availableUsdc)}
                  {t.triggeredBy ? ` · ${t.triggeredBy}` : ''}
                </span>
                <span className="text-tx3 text-xs">
                  {t.timestamp ? timeAgo(t.timestamp) : ''}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// REVERSAL QUEUE
// =============================================================================

const ReversalQueue = ({ reversals, canAct, onAction, loading }) => {
  if (loading && reversals.length === 0) {
    return (
      <div className="card mb-4">
        <div className="card-title">Reversal Queue</div>
        <div className="skeleton skeleton-block" style={{ height: 100, marginTop: 12 }} />
      </div>
    );
  }
  return (
    <div className="card mb-4">
      <div className="card-title">Reversal Queue ({reversals.length})</div>
      {reversals.length === 0 ? (
        <div className="text-tx3 text-sm" style={{ marginTop: 12 }}>
          No reversals awaiting action.
        </div>
      ) : (
        <div className="stagger" style={{ display: 'grid', gap: 8, marginTop: 12 }}>
          {reversals.map((r) => {
            const catMeta = CATEGORY_BADGE[r.category] || CATEGORY_BADGE.unclassified;
            return (
              <div
                key={r.reversalId}
                className="flag-row"
                style={{ flexDirection: 'column', alignItems: 'stretch' }}
              >
                <div className="row row-gap-3" style={{ alignItems: 'center' }}>
                  <span className={clsx('badge', catMeta.cls)} style={{ minWidth: 160, textAlign: 'center' }}>
                    {catMeta.label}
                  </span>
                  <div className="col col-gap-2 flex-1">
                    <div style={{ color: 'var(--tx1)', fontWeight: 600, fontSize: 13 }}>
                      {fmtUsdc(r.amountUsdc)} · tx {r.transactionId}
                    </div>
                    <div className="text-tx3 text-xs">
                      Trigger: {r.trigger} · {r.reasonCode}
                    </div>
                  </div>
                  <span className={clsx('badge', REVERSAL_STATE_BADGE[r.state] || 'badge-muted')}>
                    {r.state}
                  </span>
                  <span className="text-tx3 text-xs" style={{ minWidth: 70, textAlign: 'right' }}>
                    {r.createdAt ? timeAgo(r.createdAt) : ''}
                  </span>
                </div>
                {canAct && (r.state === 'in_progress' || r.state === 'requested') && (
                  <div className="row row-gap-2" style={{ flexWrap: 'wrap', marginTop: 12 }}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      style={{ padding: '6px 12px', fontSize: 12 }}
                      onClick={() => onAction(r.reversalId, 'approve')}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      style={{ padding: '6px 12px', fontSize: 12 }}
                      onClick={() => onAction(r.reversalId, 'deny')}
                    >
                      Deny
                    </button>
                  </div>
                )}
                {!canAct && (
                  <div className="text-tx3 text-xs" style={{ marginTop: 8 }}>
                    Compliance officer or super-admin role required to act on reversals.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// CO-SIGN QUEUE
// =============================================================================

const CosignQueue = ({ cosigns, loading }) => {
  if (loading && cosigns.length === 0) {
    return (
      <div className="card mb-4">
        <div className="card-title">Pending Safe Co-Signs</div>
        <div className="skeleton skeleton-block" style={{ height: 100, marginTop: 12 }} />
      </div>
    );
  }
  return (
    <div className="card mb-4">
      <div className="card-title">Pending Safe Co-Signs ({cosigns.length})</div>
      <div className="text-tx3 text-xs" style={{ marginTop: 4 }}>
        Disbursements and refunds queued for 2-of-N co-sign on the Safe Web UI.
      </div>
      {cosigns.length === 0 ? (
        <div className="text-tx3 text-sm" style={{ marginTop: 12 }}>
          No pending co-signs.
        </div>
      ) : (
        <table style={{ width: '100%', marginTop: 12, fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--tx3)', fontSize: 12 }}>
              <th style={{ padding: '8px 4px' }}>Disbursement / Reversal</th>
              <th style={{ padding: '8px 4px' }}>Amount</th>
              <th style={{ padding: '8px 4px' }}>Recipient</th>
              <th style={{ padding: '8px 4px' }}>Signers</th>
              <th style={{ padding: '8px 4px', textAlign: 'right' }}>Pending</th>
            </tr>
          </thead>
          <tbody>
            {cosigns.map((c) => (
              <tr key={c.safeTxHash} style={{ borderTop: '1px solid var(--bd)' }}>
                <td style={{ padding: '8px 4px', fontFamily: 'var(--mono)', fontSize: 12 }}>
                  {c.disbursementId || c.reversalId || '—'}
                </td>
                <td style={{ padding: '8px 4px' }}>{fmtUsdc(c.amountUsdc)}</td>
                <td style={{ padding: '8px 4px', fontFamily: 'var(--mono)', fontSize: 11 }}>
                  {c.recipient ? `${c.recipient.slice(0, 8)}…${c.recipient.slice(-6)}` : '—'}
                </td>
                <td style={{ padding: '8px 4px' }}>
                  {c.signersPresent ?? 0} / {c.signersRequired ?? 2}
                </td>
                <td style={{ padding: '8px 4px', textAlign: 'right' }}>
                  {c.proposedAt ? timeAgo(c.proposedAt) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default Treasury;
